import { useQuery } from "@tanstack/react-query";
import type { BillingPlanResponse, StripeSubscriptionPlanKey } from "@nfp-tracker/utils";
import { invokeEdgeFunction } from "../integrations/supabase/functions";
import { isCheckoutPlanKey } from "../utils/subscriptionPage";

export type BillingPlanDisplay = {
  displayName: string;
  priceDisplay: string;
  interval: "month" | "year";
};

export type BillingPlansData = {
  /** Every checkout plan, keyed by plan key. Both monthly and annual are guaranteed present. */
  planRecord: Record<StripeSubscriptionPlanKey, BillingPlanResponse>;
  byPriceId: Record<string, BillingPlanDisplay>;
  byPlanKey: Partial<Record<StripeSubscriptionPlanKey, BillingPlanDisplay>>;
  hasFounderPricing: boolean;
};

export const BILLING_PLANS_QUERY_KEY = ["billing-plans"] as const;

// The Stripe catalog is effectively static — prices change on the order of never — so this is
// cached for the session rather than refetched every time Settings or Subscription mounts.
const BILLING_PLANS_STALE_MS = 60 * 60 * 1000;

const toDisplay = (plan: BillingPlanResponse): BillingPlanDisplay => ({
  displayName: plan.displayName,
  priceDisplay: plan.priceDisplay,
  interval: plan.interval,
});

const fetchBillingPlans = async (): Promise<BillingPlansData> => {
  const { data, error } = await invokeEdgeFunction("get-billing-plans");
  if (error) throw error;

  const planRecord: Partial<Record<StripeSubscriptionPlanKey, BillingPlanResponse>> = {};
  const byPriceId: Record<string, BillingPlanDisplay> = {};
  const byPlanKey: Partial<Record<StripeSubscriptionPlanKey, BillingPlanDisplay>> = {};

  for (const plan of data?.plans ?? []) {
    byPriceId[plan.stripePriceId] = toDisplay(plan);
    if (!isCheckoutPlanKey(plan.planKey)) continue;
    planRecord[plan.planKey] = plan;
    byPlanKey[plan.planKey] = toDisplay(plan);
  }

  if (!planRecord.monthly || !planRecord.annual) {
    throw new Error("Billing plans response is missing monthly or annual plan");
  }

  return {
    planRecord: planRecord as Record<StripeSubscriptionPlanKey, BillingPlanResponse>,
    byPriceId,
    byPlanKey,
    hasFounderPricing: Boolean(data?.hasFounderPricing ?? data?.hasPerpetualDiscount),
  };
};

/**
 * Shared loader for the Stripe plan catalog.
 *
 * `get-billing-plans` requires a user JWT, so callers must gate `enabled` on the session being
 * resolved — firing it during the auth-rehydration window sends the anon key instead and gets a
 * 401 back, which surfaces as a Sentry event and (previously) a toast. Callers should also gate
 * on actually needing the data: nothing renders plan pricing when subscription management is
 * unavailable.
 */
export const useBillingPlans = ({ enabled }: { enabled: boolean }) =>
  useQuery({
    queryKey: BILLING_PLANS_QUERY_KEY,
    queryFn: fetchBillingPlans,
    enabled,
    staleTime: BILLING_PLANS_STALE_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });
